/**
 * Preferences › Sounds (settings pocket spec §6): whether finished tasks and
 * questions play a sound, which sound (with a preview), and how loud.
 * Everything applies at once.
 */
import { useState } from "react";
import {
  Button,
  PocketRow,
  PocketSection,
  SelectField,
  Slider,
  ToggleSwitch,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import {
  settingsPreferencesMessages,
  SOUND_LABEL_KEY,
} from "@/i18n/messages/settings-preferences";
import {
  getSoundType,
  getSoundVolume,
  isSoundEnabled,
  playSound,
  setSoundEnabled,
  setSoundType,
  setSoundVolume,
  SOUND_PRESETS,
  type SoundType,
} from "@/lib/notification-sound";

export function SoundsPage() {
  const t = useT(settingsPreferencesMessages);
  const [enabled, setEnabled] = useState(isSoundEnabled);
  const [volume, setVolume] = useState(getSoundVolume);
  const [sound, setSound] = useState<SoundType>(getSoundType);

  return (
    <PocketSection testId="settings-page-preferences-sounds">
      <PocketRow
        testId="settings-row-sounds-enabled"
        title={t("notificationSounds")}
        secondLine={t("notificationSoundsDetail")}
        trailing={
          <ToggleSwitch
            checked={enabled}
            onCheckedChange={(next) => {
              setEnabled(next);
              setSoundEnabled(next);
            }}
            aria-label={t("notificationSounds")}
            data-testid="sounds-enabled-toggle"
          />
        }
      />
      {enabled ? (
        <>
          <PocketRow
            testId="settings-row-sounds-sound"
            title={t("sound")}
            trailing={
              <>
                <Button
                  variant="ghost"
                  size="iconSm"
                  aria-label={t("playSound", {
                    sound: t(SOUND_LABEL_KEY[sound]),
                  })}
                  onClick={() => playSound(sound, volume)}
                  className="hover:bg-[var(--color-interactive-hover)]"
                  data-testid="sounds-preview"
                >
                  <Icons.Play className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
                <SelectField
                  variant="value"
                  value={sound}
                  options={SOUND_PRESETS.map((preset) => ({
                    value: preset.type,
                    label: t(SOUND_LABEL_KEY[preset.type]),
                  }))}
                  onChange={(next) => {
                    setSound(next as SoundType);
                    setSoundType(next as SoundType);
                  }}
                  testId="sounds-sound"
                  ariaLabel={t("sound")}
                />
              </>
            }
          />
          <PocketRow
            testId="settings-row-sounds-volume"
            title={t("volume")}
            trailing={
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={volume}
                onValueChange={(next) => {
                  setVolume(next);
                  setSoundVolume(next);
                }}
                aria-label={t("volume")}
                className="w-[140px]"
                data-testid="sounds-volume"
              />
            }
          />
        </>
      ) : null}
    </PocketSection>
  );
}
